package com.devi3east.bashbuddy.auth.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.devi3east.bashbuddy.auth.capability.AuthCapability
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.auth.domain.repositories.AuthRepository
import com.devi3east.bashbuddy.auth.requests.SignInRequest
import com.devi3east.bashbuddy.auth.requests.SignUpRequest
import com.devi3east.bashbuddy.auth.responses.AuthResult
import com.devi3east.bashbuddy.account.organization.domain.models.Organization
import com.devi3east.bashbuddy.account.organization.domain.models.StripeAccount
import com.devi3east.bashbuddy.account.organization.requests.UpdateOrganizationRequest
import com.devi3east.bashbuddy.preferences.AppPreferences
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.account.profile.requests.UpdateProfileRequest
import com.devi3east.bashbuddy.routes.Routes
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AuthViewModel(
    val repository: AuthRepository,
    private val preferences: AppPreferences,
    private val authCapability: AuthCapability
) : ViewModel() {

    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    private val _uiEvent = MutableSharedFlow<AuthUiEvent>()
    val uiEvent: SharedFlow<AuthUiEvent> = _uiEvent.asSharedFlow()

    fun signUp(request: SignUpRequest) {
        if (!authCapability.canAuthenticate()) return
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            when (val result = repository.signUp(request)) {
                is AuthResult.Success -> {
                    _uiState.update { it.copy(isLoading = false) }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Account created. Please sign in."))
                    _uiEvent.emit(AuthUiEvent.NavigateToSignIn)
                }
                is AuthResult.Authorized -> {
                    val profile = result.data!!
                    val token = repository.getAuthToken().orEmpty()

                    _uiState.update { it.copy(isLoading = false, token = token, profile = profile) }
                    _uiEvent.emit(AuthUiEvent.NavigateToEventsScreen)
                }
                is AuthResult.EmailBlank -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Email cannot be blank") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Email is required"))
                }
                is AuthResult.PasswordBlank -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Password cannot be blank") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Password is required"))
                }
                is AuthResult.InvalidCredentials -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Invalid Credentials") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Invalid credentials"))
                }
                is AuthResult.UserAlreadyExists -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "An account is already associated with the email provided.") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("User with this email already exists"))
                }
                is AuthResult.UnknownError, is AuthResult.Unauthorized -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Sign up failed") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Something went wrong"))
                }
            }
        }
    }

    fun onSignUpSelected() {
        viewModelScope.launch {
            if (authCapability.canAuthenticate()) {
                _uiEvent.emit(AuthUiEvent.NavigateToSignUp)
            } else {
                _uiEvent.emit(AuthUiEvent.OpenExternalSignUp(Routes.signUp()))
            }
        }
    }

    fun signIn(request: SignInRequest) {
        if (!authCapability.canAuthenticate()) {
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            when (val result = repository.signIn(request)) {
                is AuthResult.Authorized -> {
                    val profile = result.data
                    val token = result.accessToken
                    if (profile != null && !token.isNullOrBlank()) {
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                token = token,
                                profile = profile,
                                isActive = profile.isActive
                            )
                        }
                        if (profile.userType == UserType.ORGANIZATION) {
                            val organization = repository.getOrganizationById(profile._id)
                            _uiState.update {
                                it.copy(
                                    organization = organization,
                                    isStripeSetupStarted = organization?.stripeAccount != null
                                )
                            }
                        } else {
                            _uiState.update { it.copy(organization = null) }
                        }
                        _uiEvent.emit(AuthUiEvent.NavigateToEventsScreen)
                    } else {
                        _uiState.update {
                            it.copy(isLoading = false, errorMessage = "Invalid login response")
                        }
                        _uiEvent.emit(AuthUiEvent.ShowMessage("Login failed: missing profile or token"))
                    }
                }
                is AuthResult.InvalidCredentials -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Invalid Credentials") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Invalid email or password"))
                }
                is AuthResult.UserAlreadyExists -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "An account is already associated with the email provided.") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("User with this email already exists"))
                }
                is AuthResult.EmailBlank -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Email cannot be blank") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Email is required"))
                }
                is AuthResult.PasswordBlank -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Password cannot be blank") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Password is required"))
                }
                is AuthResult.UnknownError, is AuthResult.Unauthorized -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Login failed") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Something went wrong"))
                }
                is AuthResult.Success -> {
                    _uiState.update { it.copy(isLoading = false, errorMessage = "Unexpected result") }
                    _uiEvent.emit(AuthUiEvent.ShowMessage("Login failed: unexpected response"))
                }
            }
        }
    }

    fun onSignInSelected() {
        viewModelScope.launch {
            if (authCapability.canAuthenticate()) {
                _uiEvent.emit(AuthUiEvent.NavigateToSignIn)
            } else {
                _uiEvent.emit(AuthUiEvent.OpenExternalSignIn(Routes.signIn()))
            }
        }
    }

    fun signOut() {
        viewModelScope.launch {
            repository.signOut()
            _uiState.value = AuthUiState()
            _uiEvent.emit(AuthUiEvent.SignedOut)
        }
    }

    fun fetchProfile(userId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            val result = repository.fetchProfile(userId)
            result.onSuccess { profile ->
                _uiState.update { it.copy(profile = profile, isLoading = false) }
            }.onFailure { error ->
                _uiState.update { it.copy(errorMessage = error.message ?: "Unknown error", isLoading = false) }
            }
        }
    }

    fun updateProfile(updatedProfile: Profile) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            val request = UpdateProfileRequest(
                displayName = updatedProfile.displayName ?: "",
                name = updatedProfile.name,
                phone = updatedProfile.phone ?: "",
                profileImageUrl = updatedProfile.profileImageUrl,
                address = updatedProfile.address,
                showCartInfoDialog = updatedProfile.showCartInfoDialog,
                showFirstSignInDialog = updatedProfile.showFirstSignInDialog,
                affiliatedOrganization = updatedProfile.affiliatedOrganization
            )
            val success = repository.updateProfile(request)
            if (success) {

                val refreshed = repository.fetchProfile(updatedProfile._id)

                refreshed.onSuccess { profile ->
                    _uiState.update {
                        it.copy(
                            profile = profile,
                            isLoading = false
                        )
                    }

                    _uiState.update { it.copy(errorMessage = null) }

                }.onFailure { error ->
                    _uiState.update {
                        it.copy(
                            errorMessage = "⚠️ Update succeeded but refresh failed: ${error.message}",
                            isLoading = false
                        )
                    }
                }

            } else {
                _uiState.update {
                    it.copy(
                        errorMessage = "❌ Profile update failed",
                        isLoading = false
                    )
                }
            }
        }
    }

    private fun authenticate() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            when (val result = repository.authenticate()) {
                is AuthResult.Authorized -> {
                    val profile = result.data!!
                    val token = repository.getAuthToken()
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            token = token,
                            profile = profile,
                            isActive = profile.isActive
                        )
                    }
                    if (profile.userType == UserType.ORGANIZATION) {
                        val org = repository.getOrganizationById(profile._id)
                        _uiState.update {
                            it.copy(
                                organization = org,
                                isStripeSetupStarted = org?.stripeAccount != null
                            )
                        }
                    }
                }
                else -> {
                    _uiState.update { AuthUiState(errorMessage = "Failed to authenticate", isLoading = false) }
                }
            }
        }
    }

    suspend fun safeAuthenticate(): AuthResult<Profile> {
        val token = repository.getAuthToken()
        if (token.isNullOrBlank()) {
            return AuthResult.Unauthorized()
        }

        return when (val result = repository.authenticate()) {
            is AuthResult.Authorized -> result
            is AuthResult.Unauthorized -> {
                // Token invalid/expired, clear auth
                repository.setAuthToken(null)
                AuthResult.Unauthorized()
            }
            else -> result
        }
    }

    fun setHydrating(isHydrating: Boolean) {
        _uiState.update { it.copy(isHydrating = isHydrating) }
    }

    fun clearError() {
        _uiState.update { it.copy(errorMessage = null) }
    }

    fun setToken(token: String) {
        _uiState.update { it.copy(token = token) }
    }

    fun setProfile(profile: Profile?) {
        _uiState.update { it.copy(profile = profile) }
    }

    fun setOrganization(organization: Organization?) {
        _uiState.update { it.copy(organization = organization) }
    }

    fun loadOrganizations() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val list = repository.getOrganizationList()
            _uiState.update { it.copy(isLoading = false, organizations = list) }
        }
    }

    fun loadLeaderboard() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val list = repository.getLeaderboard()
            _uiState.update { it.copy(isLoading = false, leaderboard = list) }
        }
    }

    fun loadOrganizationData(organizationId: String) {
        viewModelScope.launch {

            val token = repository.getAuthToken()

            // 🚫 HARD BLOCK: no auth, no request
            if (token.isNullOrBlank()) {
                _uiState.update {
                    it.copy(
                        organization = null,
                        isLoading = false,
                        errorMessage = "Unauthorized session"
                    )
                }
                return@launch
            }

            _uiState.update { it.copy(isLoading = true, errorMessage = null) }

            val organization = repository.getOrganizationById(organizationId)

            if (organization != null) {
                _uiState.update { it.copy(organization = organization, isLoading = false) }
            } else {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = "❌ Failed to load organization"
                    )
                }
            }
        }
    }

    fun updateOrganizationInfo(updatedOrg: Organization) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            val request = UpdateOrganizationRequest(
                organizationName = updatedOrg.organizationName ?: "",
                organizationBio = updatedOrg.organizationBio,
                contactPerson = updatedOrg.contactPerson ?: "",
                phone = updatedOrg.phone ?: "",
                address = updatedOrg.address,
                showFirstEventCreatedDialog = updatedOrg.showFirstEventCreatedDialog
            )
            val success = repository.updateOrganization(request)
            if (success) {
                _uiState.update {
                    it.copy(
                        organization = updatedOrg,
                        errorMessage = "✅ Updated successfully",
                        isLoading = false
                    )
                }
            } else {
                _uiState.update { it.copy(errorMessage = "❌ Update failed", isLoading = false) }
            }
        }
    }

    fun updateStripeAccountPlaceholder() {
        val currentOrg = _uiState.value.organization ?: return

        val updated = currentOrg.copy(
            stripeAccount = StripeAccount(
                accountId = "pending",
                onboardingComplete = false,
                currentlyDue = emptyList(),
                eventuallyDue = emptyList()
            )
        )

        _uiState.update {
            it.copy(organization = updated)
        }
    }

    fun markStripeSetupStarted() {
        _uiState.update { it.copy(isStripeSetupStarted = true) }
    }

    // -----------------------------
    // Password Reset
    // -----------------------------
    fun sendPasswordReset(email: String) {
        viewModelScope.launch {
            try {
                repository.sendPasswordReset(email)
                _uiEvent.emit(AuthUiEvent.ShowMessage("✅ Password reset email sent to $email"))
            } catch (e: Exception) {
                _uiEvent.emit(AuthUiEvent.ShowMessage("❌ Failed to send reset email: ${e.message}"))
            }
        }
    }

    fun updatePassword(newPassword: String, recoveryToken: String) {
        viewModelScope.launch {
            try {
                repository.updatePassword(newPassword, recoveryToken)
                _uiEvent.emit(AuthUiEvent.ShowMessage("✅ Password updated successfully"))
            } catch (e: Exception) {
                val message = e.message ?: "An unexpected error occurred"
                _uiEvent.emit(AuthUiEvent.ShowMessage("❌ Failed to update password: $message"))
            }
        }
    }

    fun parseParamFromDeepLink(raw: String, param: String): String {
        if (raw.isEmpty()) return ""
        val fragment = raw.substringAfter('#', missingDelimiterValue = "")
        val fragmentValue = if (fragment.isNotEmpty()) {
            fragment.substringAfter("$param=", "").substringBefore('&').substringBefore('/')
        } else ""
        if (fragmentValue.isNotEmpty()) return fragmentValue

        val query = raw.substringAfter('?', missingDelimiterValue = raw) // if no '?', raw might itself be query-like
        val value = query.substringAfter("$param=", "").substringBefore('&').substringBefore('#').substringBefore('/')
        return value
    }

    fun tokenIsMissing(t: String?): Boolean {
        if (t == null) return true
        val trimmed = t.trim()
        if (trimmed.isEmpty()) return true
        if (trimmed.equals("null", ignoreCase = true)) return true
        return false
    }

    // --- Deactivate Account ---
    private fun deactivateAccount() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val success = repository.deactivateAccount()
            if (success) {
                _uiState.update { it.copy(isLoading = false, isActive = false) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Account deactivated"))
            } else {
                _uiState.update { it.copy(isLoading = false) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Failed to deactivate account"))
            }
        }
    }

    // --- Reactivate Account ---
    private fun reactivateAccount() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val success = repository.reactivateAccount()
            if (success) {
                _uiState.update { it.copy(isLoading = false, isActive = true) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Account reactivated"))
            } else {
                _uiState.update { it.copy(isLoading = false) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Failed to reactivate account"))
            }
        }
    }

    // --- Toggle Account Activation ---
    fun toggleAccountActivation() {
        val currentlyActive = uiState.value.isActive
        if (currentlyActive) {
            deactivateAccount()
        } else {
            reactivateAccount()
        }
    }

    // --- Delete Account ---
    fun deleteAccount() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val success = repository.deleteAccount()
            if (success) {
                _uiState.update { it.copy(isLoading = false) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Account deleted successfully"))
            } else {
                _uiState.update { it.copy(isLoading = false) }
                _uiEvent.emit(AuthUiEvent.ShowMessage("Failed to delete account"))
            }
        }
    }

}